import { useTranslation } from 'react-i18next';
// OperatorVehiclesPanel.jsx
//
// Vista inversa de VehicleOperatorsPanel: muestra qué VEHÍCULOS tiene
// asignados UN operador, y permite asignar (a un vehículo nuevo) o quitar.
// Se usa en el dialog "Vehículos asignados" desde OperatorsPage.
//
// Validaciones:
//   - El backend rechaza si el vehículo destino ya tiene 2 operadores.
//   - El backend rechaza si vehículo no pertenece a la misma empresa.
//   - El usuario sólo ve sus propios vehículos (api.vehicles.getAll).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Button,
  alpha,
  useTheme,
} from '@mui/material';
import {
  DirectionsCar as VehicleIcon,
  Add as AddIcon,
  LinkOff as UnlinkIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

import { useNotification } from '../common/GlobalNotifications';
import { api } from '../../services/api';

const OperatorVehiclesPanel = ({ operator, canEdit = true, onChanged }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { showSuccess, showError } = useNotification();

  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);

  const [allVehicles, setAllVehicles] = useState([]);
  const [pickerAnchor, setPickerAnchor] = useState(null);
  const [search, setSearch] = useState('');

  // Mismo criterio que VehicleOperatorsPanel: `onChanged` llega como funcion
  // inline del padre, asi que no puede entrar en las dependencias de `refresh`
  // o el efecto se dispararia en bucle (fetch -> setState del padre -> nueva
  // funcion -> fetch...). Se guarda en un ref.
  const onChangedRef = useRef(onChanged);
  useEffect(() => { onChangedRef.current = onChanged; }, [onChanged]);

  const refresh = useCallback(async () => {
    if (!operator?.id) return;
    setLoading(true);
    try {
      const r = await api.operators.listVehiclesForOperator(operator.id);
      setAssignments(r.data || []);
      onChangedRef.current?.(r.data || []);
    } catch (e) {
      showError(t('hardcoded.components_operators_operatorvehiclespanel.no_se_pudieron_cargar_los'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operator?.id, showError]);

  useEffect(() => { refresh(); }, [refresh]);

  const openPicker = async (evt) => {
    if (!canEdit) return;
    setPickerAnchor(evt.currentTarget);
    try {
      const r = await api.vehicles.getAll();
      // Soporta tanto `r.data` array como `r.data.vehicles`.
      const list = Array.isArray(r.data) ? r.data : (r.data?.vehicles || r.data?.items || []);
      setAllVehicles(list);
    } catch (e) {
      showError(t('hardcoded.components_operators_operatorvehiclespanel.no_se_pudieron_cargar_vehiculos'));
    }
  };

  const closePicker = () => { setPickerAnchor(null); setSearch(''); };

  const handleAssign = async (vehicleId) => {
    try {
      await api.operators.assignToVehicle(vehicleId, operator.id, null);
      showSuccess(t('hardcoded.components_operators_operatorvehiclespanel.operador_asignado_al_vehiculo'));
      closePicker();
      refresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || t('hardcoded.components_operators_operatorvehiclespanel.no_se_pudo_asignar');
      showError(msg);
    }
  };

  const handleUnassign = async (vehicleId) => {
    try {
      await api.operators.unassignFromVehicle(vehicleId, operator.id);
      showSuccess(t('hardcoded.components_operators_operatorvehiclespanel.asignacion_removida'));
      refresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || t('hardcoded.components_operators_operatorvehiclespanel.no_se_pudo_quitar');
      showError(msg);
    }
  };

  const assignedVehicleIds = useMemo(
    () => new Set(assignments.map((a) => String(a.vehicle_id))),
    [assignments]
  );

  const filteredVehicles = useMemo(() => {
    const q = (search || '').toLowerCase().trim();
    return allVehicles
      .filter((v) => !assignedVehicleIds.has(String(v.id)))
      .filter((v) => {
        if (!q) return true;
        const hay = `${v.alias || ''} ${v.make || ''} ${v.model || ''} ${v.display_name || ''}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 60);
  }, [allVehicles, search, assignedVehicleIds]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          {t('hardcoded.components_operators_operatorvehiclespanel.vehiculos_asignados')}<Chip size="small" sx={{ ml: 1, height: 20 }} label={assignments.length} />
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('hardcoded.components_operators_operatorvehiclespanel.recargar')}>
            <IconButton size="small" onClick={refresh}><RefreshIcon fontSize="small" /></IconButton>
          </Tooltip>
          {canEdit && (
            <Button size="small" startIcon={<AddIcon />} variant="contained" onClick={openPicker}>
              {t('hardcoded.components_operators_operatorvehiclespanel.asignar_a_vehiculo')}</Button>
          )}
        </Stack>
      </Stack>

      {loading ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      ) : assignments.length === 0 ? (
        <Box sx={{ p: 2, textAlign: 'center', background: alpha(theme.palette.warning.main, 0.06), borderRadius: 1, border: '1px dashed', borderColor: 'divider' }}>
          <VehicleIcon sx={{ fontSize: 32, color: 'text.disabled' }} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('hardcoded.components_operators_operatorvehiclespanel.este_operador_aun_no_esta')}</Typography>
        </Box>
      ) : (
        <List dense disablePadding>
          {assignments.map((a) => (
            <ListItem
              key={a.id}
              sx={{
                px: 1,
                py: 1,
                mb: 0.5,
                borderRadius: 1,
                background: alpha(theme.palette.success.main, 0.06),
                border: '1px solid',
                borderColor: alpha(theme.palette.success.main, 0.2),
              }}
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <VehicleIcon fontSize="small" />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body2" fontWeight={700}>
                      {a.vehicle_display_name || a.vehicle_alias || t('hardcoded.components_operators_operatorvehiclespanel.vehiculo_avehicle_idslic', { avehicle_idslic: a.vehicle_id.slice(0, 8) })}
                    </Typography>
                    <Chip size="small" label={`Slot ${a.slot}`} sx={{ height: 18, fontSize: '0.65rem' }} />
                  </Stack>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {[a.vehicle_make, a.vehicle_model].filter(Boolean).join(' ') || '—'}
                  </Typography>
                }
              />
              {canEdit && (
                <ListItemSecondaryAction>
                  <Tooltip title={t('hardcoded.components_operators_operatorvehiclespanel.quitar_asignacion')}>
                    <IconButton size="small" color="error" onClick={() => handleUnassign(a.vehicle_id)}>
                      <UnlinkIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              )}
            </ListItem>
          ))}
        </List>
      )}

      {canEdit && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {t('hardcoded.components_operators_operatorvehiclespanel.recuerda_cada_vehiculo_permite_1')}</Typography>
      )}

      {/* Picker para asignar a un vehículo nuevo */}
      <Menu
        anchorEl={pickerAnchor}
        open={Boolean(pickerAnchor)}
        onClose={closePicker}
        slotProps={{ paper: { sx: { width: 360, p: 1, maxHeight: 460 } } }}
      >
        <Box sx={{ px: 1, pb: 1 }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder={t('hardcoded.components_operators_operatorvehiclespanel.buscar_vehiculo_por_alias_marca')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </Box>
        {filteredVehicles.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="caption">
              {allVehicles.length === 0 ? t('hardcoded.components_operators_operatorvehiclespanel.no_hay_vehiculos_en_esta') : t('hardcoded.components_operators_operatorvehiclespanel.sin_vehiculos_disponibles_para_asignar')}
            </Typography>
          </MenuItem>
        ) : filteredVehicles.map((v) => (
          <MenuItem key={v.id} onClick={() => handleAssign(v.id)}>
            <ListItemAvatar>
              <Avatar sx={{ width: 28, height: 28 }}><VehicleIcon fontSize="small" /></Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={<Typography variant="body2">{v.display_name || v.alias || t('hardcoded.components_operators_operatorvehiclespanel.vehiculo_stringvidslice0', { Stringvidslice0: String(v.id).slice(0, 8) })}</Typography>}
              secondary={[v.make, v.model].filter(Boolean).join(' ') || ''}
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default OperatorVehiclesPanel;
